import {
  useCallback,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import { useSearchParams } from 'react-router-dom';
import {
  GroupUsersResponseModel,
  GroupUserOrderProperty,
} from '@uniqkey-backend-partner/api-client';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import { IGetGroupUsersParams, TGetGroupUsersMethod } from '../../useGroupsAPI/interfaces';
import { IGroupUsersTableRow } from '../../../components/tables/GroupUsersTable';
import useGroupsAPI from '../../useGroupsAPI';

interface IPersistentFilters extends Pick<IGetGroupUsersParams, 'groupId'> {}

export interface IUseGroupUsersTableProps {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseGroupUsersTableReturn extends
  Omit<IUseTableReturn<IGroupUsersTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseTableFetchReturn<GroupUsersResponseModel>, 'data'>
{
  groupUsers: IUseTableFetchReturn<GroupUsersResponseModel>['data'];
  selectedGroupUsers: IUseTableReturn<IGroupUsersTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

interface ITablePreservationConfigs extends
  Omit<IGetGroupUsersParams,
    'page' | 'pageLength' | 'groupId' | 'orderPropertyName' | 'isDescending' | 'cancelToken' |
    'signal'
  >,
  Pick<IUseTableReturn<IGroupUsersTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetGroupUsersParams['page'];
  perPage: IGetGroupUsersParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: GroupUserOrderProperty },
  ),
  columnOrderDirection: true,
};

const useGroupUsersTable = (
  props: IUseGroupUsersTableProps,
): IUseGroupUsersTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getGroupUsers } = useGroupsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedGroupUsers,
    resetTableMethods,
    ...restTableProps
  } = useTable<IGroupUsersTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: groupUsers, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetGroupUsersParams,
    TGetGroupUsersMethod,
    GroupUsersResponseModel
  >({
    request: getGroupUsers,
    params: {
      page: activePage,
      pageLength: perPage,
      orderPropertyName: GroupUserOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof GroupUserOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    groupUsers,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedGroupUsers,
    localization,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useGroupUsersTable;
